// Premises store
// --------------------------------------------------------
import Vue from 'vue'
import api from '@/lib/api'
import {GA_EVENTS} from '@/lib/ga'
import utils from '@/store/modules/premises/utils'
import helpers from './helpers'
import filters from './filters'

const state = {
  premises: null,
  current: null
}
const getters = {
  allPremises: state => state.premises,
  premises: (state, getters, rootState, rootGetters) => {
    const actor = rootGetters.selectedActor
    return !state.premises
      ? []
      : !actor
      ? state.premises
      : state.premises.filter(premise => {
          return (
            premise.parentsId.includes(actor._id) ||
            premise.relatedActors.find(actorId => {
              const actor = getters.getActorByID(actorId)
              const ancestors = actor.ancestors || []
              return ancestors.includes(actor._id)
            })
          )
        })
  },
  filtered: (state, getters) => getters['filters/data'],
  current: state => state.current
}

const mutations = {
  set(state, premises) {
    state.premises = premises
  },
  sync(state, premise) {
    const index = state.premises.findIndex(
      _premise => _premise._id === premise._id
    )
    state.premises.splice(index, 1, premise)
  },
  add(state, premise) {
    if (!state.premises) state.premises = []
    state.premises.push(premise)
  },
  delete(state, premiseId) {
    let index = state.premises.findIndex(_premise => _premise._id === premiseId)
    state.premises.splice(index, 1)
  },
  favorite(state, {premise, value}) {
    premise.currentUser.favorited = value
  },
  setCurrent(states, premise) {
    state.current = premise
  },
  removeCurrent(state) {
    state.current = null
  },
  /* COMMENT
   ********************************************************************************* */
  addComment(state, comment) {
    state.current.comments.push(comment)
    state.current.commentCount++
    state.current.currentUser.unreadComments = 0 // Hack to prevent mark as 'new' your own premise
  },
  updateComment(state, comment) {
    const index = state.current.comments.findIndex(c => c._id === comment._id)
    state.current.comments.splice(index, 1, comment)
  },
  deleteComment(state, commentId) {
    const index = state.current.comments.findIndex(c => c._id === commentId)
    state.current.comments.splice(index, 1)
    state.current.commentCount--
  },
  voteComment(state, payload) {
    const type = payload.vote
    const comment = state.current.comments.find(
      c => c._id === payload.commentId
    )
    if (type.positiveVote) {
      comment.positiveVotes++
    } else if (
      comment.currentUser.positiveVote &&
      (type.positiveVote === false || type.negativeVote)
    ) {
      comment.positiveVotes--
    }
    comment.currentUser.positiveVote = type.positiveVote || false
    comment.currentUser.negativeVote = type.negativeVote || false
  }
}

const actions = {
  setCurrent: ({commit}, premise) => commit('setCurrent', premise),
  removeCurrent: ({commit}) => commit('removeCurrent'),
  load({commit, getters, rootGetters}) {
    const path = getters['helpers/premisesPath']
    return api.call('get', path).then(premises => {
      utils.parsePremises(premises, rootGetters)
      commit('set', premises)
    })
  },
  loadPremise({commit, getters, rootGetters}, slug) {
    const path = getters['helpers/premisesPath']
    return api.call('get', path + '/' + slug).then(premise => {
      utils.parsePremises([premise], rootGetters)
      commit('setCurrent', premise)
      commit('sync', premise)
      // Update temp topics (edit case)
      commit('premises/helpers/addTempTopics', premise.topics, {root: true})
    })
  },
  create({commit, getters, rootGetters}, payload) {
    Vue.$ga.event(GA_EVENTS.PREMISE_CREATE)
    const path = getters['helpers/actorPath'](null, payload.actorId)
    return api.call('post', path, payload).then(premise => {
      utils.parsePremises([premise], rootGetters)
      commit('add', premise)
      commit('updateLandscapeTags', premise.tags, {root: true})
      return premise
    })
  },
  edit({commit, getters, rootGetters}, payload) {
    const path = getters['helpers/premisePath'](payload.premise)
    return api.call('patch', path, payload).then(premise => {
      utils.parsePremises([premise], rootGetters)
      commit('setCurrent', premise)
      commit('updateLandscapeTags', premise.tags, {root: true})
      commit('sync', premise)
    })
  },
  delete({commit, getters}, premise) {
    const path = getters['helpers/premisePath'](premise)
    return api.call('delete', path).then(() => commit('delete', premise._id))
  },
  favorite({commit, getters}, payload) {
    commit('favorite', payload) // Optimistic
    commit('sync', payload.premise)
    const path = getters['helpers/premisePath'](payload.premise)
    return api.call('post', `${path}/favorite`, {
      favorite: payload.value
    })
  },
  addTopic({getters}, payload) {
    const premiseId = payload.premise._id
    const topics = {topics: payload.topics}
    const path = getters['helpers/actorPath'](payload.premise)
    return api.call('patch', `${path}/${premiseId}/topics`, topics)
  },
  addTopics({commit, getters, rootGetters}, payload) {
    const premiseId = payload.id
    const topics = {topics: payload.topics}
    const path = getters['helpers/actorPath'](premiseId)
    return api
      .call('put', `${path}/${premiseId}/topics`, topics)
      .then(premise => {
        utils.parsePremises([premise], rootGetters)
        commit('setCurrent', premise)
        commit('sync', premise)
        return premise
      })
  },
  /* COMMENT
   ********************************************************************************* */
  createComment({commit, getters}, payload) {
    const path = getters['helpers/commentPath']
    return api.call('post', path, payload).then(comment => {
      commit('addComment', comment)
    })
  },
  editComment({commit, getters}, payload) {
    const path = getters['helpers/commentPath']
    return api
      .call('put', `${path}/${payload.commentId}`, payload)
      .then(comment => {
        commit('updateComment', comment)
      })
  },
  deleteComment({commit, getters}, commentId) {
    const path = getters['helpers/commentPath']
    return api.call('delete', `${path}/${commentId}`).then(() => {
      commit('deleteComment', commentId)
    })
  },
  voteComment({commit, getters}, payload) {
    commit('voteComment', payload) // Optimistic
    const path = getters['helpers/commentPath']
    const commentId = payload.commentId
    return api.call('post', `${path}/${commentId}/vote/`, payload.vote)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {helpers, filters}
}
